import { z } from "zod";

import type { FieldError } from "@av/components/atoms/form/types";
import { i18nInstance } from "@av/config/i18n/i18n-bundle";

import type { AxiosError } from "axios";
import type { TFunction } from "i18next";

const getErrorCodeMapping = (t: TFunction) => {
  return {
    NotNull: {
      priority: 0,
      i18nTemplate: "recruitment.common.validation_requiredV2",
      i18nParams: {},
      label: t("recruitment.common.validation_requiredV2"),
    },
    NotBlank: {
      priority: 1,
      i18nTemplate: "recruitment.common.validation_requiredV2",
      i18nParams: {},
      label: t("recruitment.common.validation_requiredV2"),
    },
    NotEmpty: {
      priority: 2,
      i18nTemplate: "recruitment.common.validation_notEmpty",
      i18nParams: {},
      label: t("recruitment.common.validation_notEmpty"),
    },
    SelectedLocaleContentRequired: {
      priority: 100,
      i18nTemplate: "recruitment.common.validation_requiredV2",
      i18nParams: {},
      label: t("recruitment.common.validation_requiredV2"),
    },
    NonRemoteLocationRequired: {
      priority: 101,
      i18nTemplate: "recruitment.common.validation_requiredV2",
      i18nParams: {},
      label: t("recruitment.common.validation_requiredV2"),
    },
    NullOrNotBlank: {
      priority: 3,
      i18nTemplate: "recruitment.common.validation_nullOrNotBlank",
      i18nParams: {},
      label: t("recruitment.common.validation_nullOrNotBlank"),
    },
    Email: {
      priority: 4,
      i18nTemplate: "recruitment.common.validation_invalid_email",
      i18nParams: {},
      label: t("recruitment.common.validation_invalid_email"),
    },
    PhoneNumber: {
      priority: 5,
      i18nTemplate: "recruitment.common.validation_invalid_phoneNumber",
      i18nParams: {},
      label: t("recruitment.common.validation_invalid_phoneNumber"),
    },
    LinkedInUrl: {
      priority: 6,
      i18nTemplate: "recruitment.common.validation_invalid_linkedInUrl",
      i18nParams: {},
      label: t("recruitment.common.validation_invalid_linkedInUrl"),
    },
    CompleteUrl: {
      priority: 7,
      i18nTemplate: "recruitment.common.validation_invalid_completeUrl",
      i18nParams: {},
      label: t("recruitment.common.validation_invalid_completeUrl"),
    },
    Size: {
      priority: 8,
      i18nTemplate: "recruitment.common.validation_size",
      i18nParams: {},
      label: t("recruitment.common.validation_size"),
    },
    CompensationRequiredWhenIncludedOnJobPost: {
      priority: 9,
      i18nTemplate:
        "recruitment.common.validation_compensation_compensationRequiredWhenIncludedOnJobPost",
      i18nParams: {},
      label: t(
        "recruitment.common.validation_compensation_compensationRequiredWhenIncludedOnJobPost"
      ),
    },
    MinCompensationRequired: {
      priority: 10,
      i18nTemplate: "recruitment.common.validation_compensation_minCompensationRequired",
      i18nParams: {},
      label: t("recruitment.common.validation_compensation_minCompensationRequired"),
    },
    MaxCompensationRequired: {
      priority: 11,
      i18nTemplate: "recruitment.common.validation_compensation_maxCompensationRequired",
      i18nParams: {},
      label: t("recruitment.common.validation_compensation_maxCompensationRequired"),
    },
    InvalidCompensationRange: {
      priority: 12,
      i18nTemplate: "recruitment.common.validation_compensation_invalidCompensationRange",
      i18nParams: {},
      label: t("recruitment.common.validation_compensation_invalidCompensationRange"),
    },
    InvalidCompensationAmount: {
      priority: 103,
      i18nTemplate: "recruitment.common.validation_compensation_invalidCompensationAmount",
      i18nParams: {},
      label: t("recruitment.common.validation_compensation_invalidCompensationAmount"),
    },
    SalaryCompensationMustBeWholeNumber: {
      priority: 102,
      i18nTemplate:
        "recruitment.common.validation_compensation_salaryCompensationMustBeWholeNumber",
      i18nParams: {},
      label: t("recruitment.common.validation_compensation_salaryCompensationMustBeWholeNumber"),
    },
    AnsweredQuestionCannotBeRemoved: {
      priority: 13,
      i18nTemplate:
        "recruitment.common.validation_screeningQuestion_answeredQuestionCannotBeRemoved",
      i18nParams: {},
      label: t("recruitment.common.validation_screeningQuestion_answeredQuestionCannotBeRemoved"),
    },
    UnansweredQuestionCannotBeDeactivated: {
      priority: 14,
      i18nTemplate:
        "recruitment.common.validation_screeningQuestion_unansweredQuestionCannotBeDeactivated",
      i18nParams: {},
      label: t(
        "recruitment.common.validation_screeningQuestion_unansweredQuestionCannotBeDeactivated"
      ),
    },
    ApplyByDateTooSoon: {
      priority: 15,
      i18nTemplate: "recruitment.common.validation_applyByDateTooSoon",
      i18nParams: {},
      label: t("recruitment.common.validation_applyByDateTooSoon"),
    },
    IncompleteAnswers: {
      priority: 16,
      i18nTemplate: "recruitment.jobApplication.screeningQuestionsChangedError",
      i18nParams: {},
      label: t("recruitment.jobApplication.screeningQuestionsChangedError"),
    },
    DuplicateManagedResume: {
      priority: 17,
      i18nTemplate: "recruitment.talentPool.candidateProfile_documents_uploadDuplicateResume",
      i18nParams: {},
      label: t("recruitment.talentPool.candidateProfile_documents_uploadDuplicateResume"),
    },
    PlainText: {
      priority: 18,
      i18nTemplate: "recruitment.common.validation_plainText",
      i18nParams: {},
      label: t("recruitment.common.validation_plainText"),
    },
    DocumentLimitReached: {
      priority: 19,
      i18nTemplate: "recruitment.talentPool.candidateProfile_documents_uploadLimitReached",
      i18nParams: {},
      label: t("recruitment.talentPool.candidateProfile_documents_uploadLimitReached"),
    },
    // Same rejection the client-side extension check reports, so it reuses that copy.
    InvalidDocumentContentType: {
      priority: 20,
      i18nTemplate: "recruitment.jobApplication.documents_fileTypeUploadError",
      i18nParams: {},
      label: t("recruitment.jobApplication.documents_fileTypeUploadError"),
    },
  } as const;
};

export const ERROR_CODE_I18N_MAPPING = getErrorCodeMapping(i18nInstance.t);

type ApiErrorCode = keyof typeof ERROR_CODE_I18N_MAPPING;

export interface ApiValidationError {
  field: string;
  code: ApiErrorCode;
  message: string;
}

export const ValidationErrorSchema = z.object({
  field: z.string(),
  code: z.string() as z.ZodType<ApiErrorCode>,
  message: z.string(),
  params: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]))
    .optional(),
});

export const ApiErrorResponseSchema = z.object({
  correlationId: z.string(),
  error: z.object({ code: z.string(), message: z.string() }),
  status: z.string(),
  statusCode: z.number(),
  timestamp: z.string(),
  validationErrors: z.array(ValidationErrorSchema),
});

export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>;
export type ValidationError = z.infer<typeof ValidationErrorSchema>;

export type ApiError = AxiosError<ApiErrorResponse>;

export function getDisplayedError(
  t: TFunction,
  code: ApiErrorCode,
  serverParams?: Record<string, string | number | boolean | string[]>
): FieldError {
  const mappedErrorCode = getErrorCodeMapping(t)[code];

  if (!mappedErrorCode) {
    console.error("Error code mapping not found");

    return { i18nTemplate: "hr.common.validation_unknown", i18nParams: { code } };
  }

  return {
    i18nTemplate: mappedErrorCode.i18nTemplate,
    i18nParams: {
      ...mappedErrorCode.i18nParams,
      ...serverParams,
    },
    priority: mappedErrorCode.priority,
  };
}
